/**
 * Route-transition scroll handler. Mounted once at the App root inside
 * <BrowserRouter>:
 *
 * - No hash → scroll to top (so route changes land at the top, not inheriting
 *   the previous route's scroll position).
 * - With hash → scroll the matching #id element into view. Browsers don't
 *   auto-scroll for SPA navigations the way they do for full-page loads, so
 *   we run scrollIntoView on the next frame (after React commits the DOM the
 *   new route renders).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.slice(1);
      // Defer to next frame so the new route's DOM is mounted before we
      // measure. Falls back to top-scroll if the anchor doesn't exist on
      // this route — better than leaving the user at a stale scroll position.
      const raf = requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        else window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return () => cancelAnimationFrame(raf);
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
}

export default ScrollToTop;
